// @ts-nocheck
import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { momentTz } from '../../utils/dates';
import { DATE_FORMAT, NOOP } from '../../const/CommonConst';
import { ReactComponent as ChevronDownIcon } from '../../assets/Icons/lucideIcons/chevron-down.svg';
import { ReactComponent as ChevronLeftIcon } from '../../assets/Icons/lucideIcons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../../assets/Icons/lucideIcons/chevron-right.svg';

const handleMouseDown = (e) => e.preventDefault();

const DatePicker = ({
  // TextField specific props
  label,
  error = false,
  helperText = '',
  disableBackspace = false,
  required = false,
  onBlur = NOOP,
  height = '36px',
  fullWidth = true,

  // DatePicker specific props
  value,
  onChange,
  maxDate = null,
  minDate = null,
  open,
  onOpen,
  onClose,
  readOnly = false,
  disabled = false,
  format = DATE_FORMAT,

  // Custom slot & styling overrides
  slots = {},
  slotProps = {},
  sx,
  ...restProps
}) => {
  const handleKeyDown = useCallback(
    (event) => {
      if (disableBackspace && event?.key === 'Backspace') {
        event.preventDefault();
      }
    },
    [disableBackspace]
  );
  const inputRef = useRef(null);

  // This logic is implemented for onBlur event as onBlur is not supported by Mui by default
  useEffect(() => {
    const handleBlur = (event) => {
      if (onBlur && !inputRef?.current?.contains(event?.relatedTarget)) {
        onBlur(event);
      }
    };

    const inputNode = inputRef.current;
    if (inputNode) {
      inputNode.addEventListener('blur', handleBlur, true);
    }

    return () => {
      if (inputNode) {
        inputNode.removeEventListener('blur', handleBlur, true);
      }
    };
  }, [onBlur]);

  const safeValue = useMemo(() => {
    if (value == null) return null;
    if (momentTz.isMoment(value)) return value;
    const m = momentTz(value);
    return m.isValid() ? m : null;
  }, [value]);

  const [isOpenState, setIsOpenState] = useState(false);
  const isOpen = open !== undefined ? open : isOpenState;

  const handleOpenInternal = useCallback(() => {
    if (readOnly) return;
    setIsOpenState(true);
    if (onOpen) onOpen();
  }, [onOpen, setIsOpenState, readOnly]);

  const handleCloseInternal = useCallback(() => {
    setIsOpenState(false);
    if (onClose) onClose();
  }, [onClose, setIsOpenState]);

  // Support both modern slotProps.textField and legacy TextFieldProps
  const consumerTextFieldProps =
    slotProps?.textField || restProps?.TextFieldProps || {};
  const {
    InputProps: consumerInputProps,
    InputLabelProps: consumerInputLabelProps,
    slotProps: consumerTextFieldSlotProps,
    sx: consumerTextFieldSx,
    ...restConsumerTextFieldProps
  } = consumerTextFieldProps;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MuiDatePicker
        {...restProps}
        value={safeValue}
        onChange={onChange}
        open={isOpen}
        onOpen={handleOpenInternal}
        onClose={handleCloseInternal}
        maxDate={maxDate}
        minDate={minDate}
        readOnly={readOnly}
        disabled={disabled}
        format={format}
        label={label}
        slots={{
          switchViewIcon: ChevronDownIcon,
          leftArrowIcon: ChevronLeftIcon,
          rightArrowIcon: ChevronRightIcon,
          ...slots,
        }}
        slotProps={{
          ...slotProps,
          openPickerButton: {
            // size: 'small',
            sx: {
              padding: '0px',
              marginRight: '0px',
              ...slotProps?.openPickerButton?.sx,
            },
            ...slotProps?.openPickerButton,
          },
          openPickerIcon: {
            sx: {
              width: 18,
              height: 18,
              ...slotProps?.openPickerIcon?.sx,
            },
            ...slotProps?.openPickerIcon,
          },
          textField: {
            label,
            error: Boolean(error),
            helperText,
            required,
            fullWidth,
            size: 'small',
            onKeyDown: handleKeyDown,
            ref: inputRef,
            onClick: handleOpenInternal,
            onMouseDown: handleMouseDown,
            ...restConsumerTextFieldProps,
            slotProps: {
              ...consumerTextFieldSlotProps,
              input: {
                readOnly,
                ...consumerInputProps,
                ...consumerTextFieldSlotProps?.input,
              },
              inputLabel: {
                shrink: true,
                ...consumerInputLabelProps,
                ...consumerTextFieldSlotProps?.inputLabel,
              },
            },
            sx: {
              // Match standard theme label spacing
              marginTop: label ? '22px' : '0px',

              // Base input container styling
              '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiPickersInputBase-root':
                {
                  height,
                  minHeight: height,
                  maxHeight: height,
                  borderRadius: '4px !important',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                  cursor: disabled ? 'default' : 'pointer',
                  paddingRight: '16px !important',
                },

              // Ensures outline border matches 4px radius and legend is hidden
              '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline':
                {
                  borderRadius: '4px !important',
                  top: '-1px',
                  '& legend': {
                    display: 'none',
                  },
                },

              // Input base element with single 8px 12px padding
              '& .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiPickersInputBase-sectionsContainer':
                {
                  cursor: disabled ? 'default' : 'pointer',
                  padding: '8px 12px !important',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  height: '100%',
                  boxSizing: 'border-box',
                },

              // Reset inner pickers section list so padding doesn't compound
              '& .MuiPickersSectionList-root': {
                cursor: disabled ? 'default' : 'pointer',
                padding: '0 !important',
                margin: '0 !important',
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: 400,
              },

              '& .MuiPickersSectionList-section, & .MuiPickersSectionList-sectionContent, & .MuiPickersSectionList-sectionSeparator':
                {
                  padding: '0 !important',
                  margin: '0 !important',
                  fontSize: '14px',
                  lineHeight: '20px',
                },

              '& .MuiInputLabel-root': {
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '125%',
                transform: 'none',
                top: '-18px',
                left: 0,
                color: '#393939 !important',
                '&.Mui-focused': {
                  color: '#393939 !important',
                },
              },

              // Calendar icon adornment centering
              '& .MuiInputAdornment-root': {
                height: '100%',
                maxHeight: '100%',
                margin: '0',
                alignItems: 'center',
              },

              ...sx,
              ...consumerTextFieldSx,
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

DatePicker.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disableBackspace: PropTypes.bool,
  required: PropTypes.bool,
  onBlur: PropTypes.func,
  height: PropTypes.string, // Added height prop type
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
  ]),
  onChange: PropTypes.func,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  maxDate: PropTypes.instanceOf(Object),
  minDate: PropTypes.instanceOf(Object),
  format: PropTypes.string,
  label: PropTypes.node,
  fullWidth: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
  slots: PropTypes.oneOfType([PropTypes.object]),
  slotProps: PropTypes.oneOfType([PropTypes.object]),
};

export default DatePicker;
